import { Fragment, useMemo, useState } from "react";
import { AlertTriangle, Ban, ChevronDown, ChevronRight, Hourglass } from "lucide-react";
import { useAdminAccess, useAdminUsage, useAdminUserUsage } from "../api/hooks";
import { ApiError } from "../api/client";
import type { DayUsage, UsagePeriod, UserCall, UserUsage } from "../api/types";
import { MONTHS_SHORT, addDays, addMonths, diffDays, nowIn, parts, startOfMonth, type DateStr } from "../lib/date";
import { useUi } from "../state/ui";
import { PageHead } from "./TasksPage";
import "./pages.css";
import "./AdminPage.css";

const PERIODS: Array<{ key: UsagePeriod; label: string; lead: string }> = [
  { key: "24h", label: "24 h", lead: "nas últimas 24 horas" },
  { key: "7d", label: "7 dias", lead: "nos últimos 7 dias" },
  { key: "30d", label: "30 dias", lead: "nos últimos 30 dias" },
  { key: "90d", label: "90 dias", lead: "nos últimos 90 dias" },
  { key: "all", label: "Tudo", lead: "desde o começo" }
];

// --- Formatting -------------------------------------------------------------------------

const intFmt = new Intl.NumberFormat("pt-BR");
const compactFmt = new Intl.NumberFormat("pt-BR", { notation: "compact", maximumFractionDigits: 1 });

/** 950 → "950", 12_345 → "12,3 mil", 1_200_000 → "1,2 mi". */
const tokens = (n: number) => (n < 10_000 ? intFmt.format(Math.round(n)) : compactFmt.format(n));

/** Small costs keep significant digits: US$ 0,0042 instead of US$ 0,00. */
function money(value: number, currency: "USD" | "BRL"): string {
  const digits = value === 0 ? 2 : value < 0.01 ? 4 : value < 1 ? 3 : 2;
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
}
const usd = (v: number) => money(v, "USD");

/** 5521980576237 → "+55 21 98057-6237". */
function phone(p: string): string {
  const m = /^55(\d{2})(\d{4,5})(\d{4})$/.exec(p);
  return m ? `+55 ${m[1]} ${m[2]}-${m[3]}` : `+${p}`;
}

const ratio = (a: number, b: number) => (b ? a / b : 0);

/** 42 → "42 s", 200 → "3 min 20 s", 5400 → "90 min". */
function duration(seconds: number): string {
  const s = Math.round(seconds);
  if (s < 60) return `${s} s`;
  const min = Math.floor(s / 60);
  return min >= 10 || s % 60 === 0 ? `${intFmt.format(Math.round(s / 60))} min` : `${min} min ${s % 60} s`;
}

/** What the call was for: a typed message, a voice note's transcription, or understanding a voice note. */
function origin(c: UserCall): string {
  if (c.kind === "transcription") return `🎧 Transcrição${c.audioSeconds !== null ? ` · ${duration(c.audioSeconds)}` : ""}`;
  return c.source === "audio" ? "🎧 Áudio" : "Texto";
}

function dateTime(iso: string, tz: string): string {
  const { date, minutes } = nowIn(tz, new Date(iso));
  const { d, m } = parts(date);
  return `${d} de ${MONTHS_SHORT[m - 1]}, ${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

// --- Page -------------------------------------------------------------------------------

/** LLM token usage and cost: totals, per-user averages, a daily chart, outliers, and a per-user table. */
export function AdminPage() {
  const ui = useUi();
  const [period, setPeriod] = useState<UsagePeriod>("30d");
  const { data, isError, isFetching } = useAdminUsage(period);
  const meta = PERIODS.find((p) => p.key === period)!;

  return (
    <div className="page-scroll">
      <div className="page wide admin">
        <PageHead label="Painel admin" title="Uso da IA">
          Tokens de entrada (input) e saída (output) por usuário, custo e sinais de uso fora do normal.
        </PageHead>

        <div className="admin-bar">
          <div className="seg" role="tablist" aria-label="Período">
            {PERIODS.map((p) => (
              <button key={p.key} role="tab" aria-selected={period === p.key} className={`seg-opt${period === p.key ? " on" : ""}`} onClick={() => setPeriod(p.key)}>
                {p.label}
              </button>
            ))}
          </div>
          {data && (
            <span className="admin-models">
              {data.models.length ? data.models.map((m) => `${m.model} (${intFmt.format(m.calls)})`).join(" · ") : "Nenhuma chamada à IA"}
              {isFetching && " · atualizando…"}
            </span>
          )}
        </div>

        {isError && !data && <p className="empty">Não foi possível carregar o uso.</p>}
        {!data && !isError && <div className="spinner" />}
        {data && (
          <>
            <Stats data={data} lead={meta.lead} />
            <AudioStats data={data} />
            <CostChart days={data.days} period={period} since={data.since} today={ui.today} />
            <Restricted users={data.users} limit={data.rateLimitPerHour} tz={ui.me.timezone} />
            <Flagged users={data.users} />
            <UsersTable users={data.users} period={period} tz={ui.me.timezone} usdBrl={data.usdBrlRate} limit={data.rateLimitPerHour} />
            <p className="admin-note">
              Custo estimado pela tabela de preços do backend (US$ por milhão de tokens, input em cache mais barato; reasoning conta como output).
              Conversão a R$ {data.usdBrlRate.toLocaleString("pt-BR")} por dólar. Mensagens resolvidas pelas regras locais não chamam a IA e não custam tokens.
              O custo de um áudio soma a transcrição e a interpretação do texto transcrito; ele já está incluído nos totais acima.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="stat">
      <span className="eyebrow">{label}</span>
      <span className="stat-value display">{value}</span>
      {sub && <span className="stat-sub">{sub}</span>}
    </div>
  );
}

function Stats({ data, lead }: { data: NonNullable<ReturnType<typeof useAdminUsage>["data"]>; lead: string }) {
  const t = data.totals;
  // Average per user: over users who sent at least one message in the period.
  const users = t.activeUsers;
  const viaRules = Math.max(t.messages - (t.calls - t.failedCalls), 0);
  return (
    <section aria-label={`Resumo ${lead}`}>
      <div className="stat-hero">
        <span className="chart-number display">{usd(t.costUsd)}</span>
        <span className="chart-lead">
          de custo {lead} · {money(t.costUsd * data.usdBrlRate, "BRL")}
        </span>
      </div>
      <div className="stat-grid">
        <Stat label="Custo médio por usuário" value={usd(ratio(t.costUsd, users))} sub={`${intFmt.format(users)} ativos de ${intFmt.format(t.totalUsers)}`} />
        <Stat label="Input médio por usuário" value={tokens(ratio(t.inputTokens, users))} sub={`${tokens(t.inputTokens)} no total`} />
        <Stat label="Output médio por usuário" value={tokens(ratio(t.outputTokens, users))} sub={`${tokens(t.outputTokens)} no total · ${tokens(t.reasoningTokens)} reasoning`} />
        <Stat label="Por chamada à IA" value={`${tokens(ratio(t.inputTokens, t.calls))} / ${tokens(ratio(t.outputTokens, t.calls))}`} sub={`input / output · ${usd(ratio(t.costUsd, t.calls))} cada`} />
        <Stat label="Chamadas à IA" value={intFmt.format(t.calls)} sub={t.failedCalls ? `${intFmt.format(t.failedCalls)} sem resposta válida` : "todas válidas"} />
        <Stat
          label="Custo por mensagem"
          value={usd(ratio(t.costUsd, t.messages))}
          sub={`${intFmt.format(t.messages)} mensagens · ${Math.round(ratio(viaRules, t.messages) * 100)}% sem IA`}
        />
      </div>
    </section>
  );
}

/** Voice notes: their transcription plus the intent calls on what was said. */
function AudioStats({ data }: { data: NonNullable<ReturnType<typeof useAdminUsage>["data"]> }) {
  const a = data.totals.audio;
  return (
    <section className="admin-audio" aria-label="Mensagens por áudio">
      <h2 className="row-section-title eyebrow">Mensagens por áudio</h2>
      <div className="stat-grid">
        <Stat
          label="Custo com áudio"
          value={usd(a.costUsd)}
          sub={`${Math.round(ratio(a.costUsd, data.totals.costUsd) * 100)}% do total · ${usd(a.transcriptionCostUsd)} em transcrição`}
        />
        <Stat
          label="Tokens de áudio"
          value={`${tokens(a.inputTokens)} / ${tokens(a.outputTokens)}`}
          sub={`input / output · ${intFmt.format(a.transcriptions)} transcrições + ${intFmt.format(a.calls - a.transcriptions)} interpretações`}
        />
        <Stat
          label="Áudios recebidos"
          value={intFmt.format(a.messages)}
          sub={a.messages ? `${duration(a.seconds)} transcritos · ${usd(ratio(a.costUsd, a.messages))} por áudio` : "nenhum no período"}
        />
      </div>
    </section>
  );
}

// --- Daily cost chart ---------------------------------------------------------------------

interface Bucket {
  key: string;
  axis: string;
  tip: string;
  costUsd: number;
  calls: number;
  inputTokens: number;
  outputTokens: number;
}

/** Fills the days without calls; more than ~3 months of days are grouped by month. */
function buckets(days: DayUsage[], period: UsagePeriod, since: string | null, today: DateStr, tz: string): Bucket[] {
  const byDay = new Map(days.map((d) => [d.day, d]));
  const span = { "24h": 2, "7d": 7, "30d": 30, "90d": 90 } as const;
  const first =
    period === "all" ? (days[0]?.day ?? today) : since ? nowIn(tz, new Date(since)).date : addDays(today, 1 - span[period as keyof typeof span]);
  const empty = (key: string, axis: string, tip: string): Bucket => ({ key, axis, tip, costUsd: 0, calls: 0, inputTokens: 0, outputTokens: 0 });
  const add = (b: Bucket, d: DayUsage | undefined) => {
    if (!d) return;
    b.costUsd += d.costUsd;
    b.calls += d.calls;
    b.inputTokens += d.inputTokens;
    b.outputTokens += d.outputTokens;
  };

  if (diffDays(first, today) <= 92) {
    const count = Math.max(diffDays(first, today) + 1, 7);
    return Array.from({ length: count }, (_, i) => {
      const day = addDays(today, i + 1 - count);
      const { d, m } = parts(day);
      const b = empty(day, `${d}/${m}`, `${d} de ${MONTHS_SHORT[m - 1]}`);
      add(b, byDay.get(day));
      return b;
    });
  }
  const out: Bucket[] = [];
  for (let month = startOfMonth(first); month <= today; month = addMonths(month, 1)) {
    const { y, m } = parts(month);
    const b = empty(month, MONTHS_SHORT[m - 1].replace(".", ""), `${MONTHS_SHORT[m - 1]} de ${y}`);
    for (const d of days) if (d.day >= month && d.day < addMonths(month, 1)) add(b, d);
    out.push(b);
  }
  return out;
}

function CostChart({ days, period, since, today }: { days: DayUsage[]; period: UsagePeriod; since: string | null; today: DateStr }) {
  const ui = useUi();
  const [hover, setHover] = useState<number | null>(null);
  const cols = useMemo(() => buckets(days, period, since, today, ui.me.timezone), [days, period, since, today, ui.me.timezone]);
  const max = Math.max(...cols.map((c) => c.costUsd), 0);
  const peak = max > 0 ? cols.findIndex((c) => c.costUsd === max) : -1;
  const tickEvery = Math.ceil(cols.length / 6);

  return (
    <section className="chart-card admin-chart" aria-label="Custo por dia">
      <div className="chart-head">
        <h2 className="col-title display">Custo por {cols.length && cols[0].key.length === 10 ? "dia" : "mês"}</h2>
      </div>
      <div className="chart-plot" onPointerLeave={() => setHover(null)}>
        <div className="chart-cols" style={{ ["--cols" as string]: cols.length }} aria-hidden>
          {cols.map((c, i) => (
            <div key={c.key} className={`chart-col${hover === i ? " hover" : ""}`} onPointerEnter={() => setHover(i)} onClick={() => setHover(i)}>
              {i === peak && hover === null && <span className="chart-cap">{usd(max)}</span>}
              <span className="chart-bar" style={{ height: max ? `${(c.costUsd / max) * 100}%` : 0 }} />
            </div>
          ))}
        </div>
        {hover !== null && (
          <div
            className="chart-tip"
            role="status"
            style={{
              left: `${((hover + 0.5) / cols.length) * 100}%`,
              transform: `translate(${hover < cols.length * 0.2 ? "-16px" : hover >= cols.length * 0.8 ? "calc(-100% + 16px)" : "-50%"}, -100%)`
            }}
          >
            <strong>{usd(cols[hover].costUsd)}</strong>
            <span>{cols[hover].tip}</span>
            <span>
              {intFmt.format(cols[hover].calls)} chamadas · {tokens(cols[hover].inputTokens)} in · {tokens(cols[hover].outputTokens)} out
            </span>
          </div>
        )}
        <div className="chart-axis" style={{ ["--cols" as string]: cols.length }} aria-hidden>
          {cols.map((c, i) => (
            <span key={c.key}>{(cols.length - 1 - i) % tickEvery === 0 ? c.axis : ""}</span>
          ))}
        </div>
      </div>
      {!max && <p className="chart-empty">Nenhum custo neste período.</p>}
      <table className="visually-hidden">
        <caption>Custo e tokens por período</caption>
        <thead>
          <tr>
            <th scope="col">Período</th>
            <th scope="col">Custo (US$)</th>
            <th scope="col">Chamadas</th>
            <th scope="col">Input</th>
            <th scope="col">Output</th>
          </tr>
        </thead>
        <tbody>
          {cols.map((c) => (
            <tr key={c.key}>
              <td>{c.tip}</td>
              <td>{usd(c.costUsd)}</td>
              <td>{c.calls}</td>
              <td>{c.inputTokens}</td>
              <td>{c.outputTokens}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// --- Access: hourly cap and manual block --------------------------------------------------

/** "Pausado" (over the hourly cap) or "Bloqueado" (by hand), with the buttons that undo or apply it. */
function AccessControls({ user, limit, tz }: { user: UserUsage; limit: number; tz: string }) {
  const access = useAdminAccess();
  const busy = access.reset.isPending || access.block.isPending;
  const error = access.reset.error ?? access.block.error;
  const blocked = user.blockedAt !== null;

  const status = blocked
    ? `Bloqueado desde ${dateTime(user.blockedAt!, tz)} — as mensagens são ignoradas sem resposta.`
    : user.rateLimited
      ? `Pausado pelo limite: ${intFmt.format(user.recentMessages)} mensagens na última hora (limite ${intFmt.format(limit)}).`
      : limit > 0
        ? `${intFmt.format(user.recentMessages)} de ${intFmt.format(limit)} mensagens na última hora.`
        : "Sem limite por hora.";

  return (
    <div className="admin-access">
      <span className={`admin-access-status${blocked || user.rateLimited ? " danger" : ""}`}>{status}</span>
      <div className="admin-access-actions">
        {!blocked && user.recentMessages > 0 && limit > 0 && (
          <button className={`btn ${user.rateLimited ? "primary" : "outline"}`} disabled={busy} onClick={() => access.reset.mutate(user.userId)}>
            Destravar limite
          </button>
        )}
        <button
          className={`btn ${blocked ? "primary" : "outline danger"}`}
          disabled={busy}
          onClick={() => access.block.mutate({ userId: user.userId, blocked: !blocked })}
        >
          {blocked ? "Desbloquear" : "Bloquear"}
        </button>
      </div>
      {error && (
        <span className="admin-access-error">
          {error instanceof ApiError && error.code === "cannot_block_admin" ? "Um admin não pode ser bloqueado." : "Não foi possível salvar. Tente de novo."}
        </span>
      )}
    </div>
  );
}

/** Whoever the assistant is ignoring right now, so they can be let through without hunting the table. */
function Restricted({ users, limit, tz }: { users: UserUsage[]; limit: number; tz: string }) {
  const restricted = users.filter((u) => u.blockedAt !== null || u.rateLimited);
  if (!restricted.length) return null;
  return (
    <section className="admin-flags" aria-label="Acesso pausado ou bloqueado">
      <h2 className="row-section-title eyebrow danger">
        Pausados ou bloqueados <span>{String(restricted.length).padStart(2, "0")}</span>
      </h2>
      <ul className="rows">
        {restricted.map((u) => (
          <li key={u.userId} className="row admin-flag">
            {u.blockedAt ? <Ban size={18} className="admin-flag-icon" aria-hidden /> : <Hourglass size={18} className="admin-flag-icon" aria-hidden />}
            <div className="admin-flag-main">
              <span className="row-title">{u.name ?? phone(u.phone)}</span>
              <AccessControls user={u} limit={limit} tz={tz} />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

// --- Outliers -----------------------------------------------------------------------------

function Flagged({ users }: { users: UserUsage[] }) {
  const flagged = users.filter((u) => u.flags.length);
  if (!flagged.length) return null;
  return (
    <section className="admin-flags" aria-label="Uso fora do normal">
      <h2 className="row-section-title eyebrow danger">
        Uso fora do normal <span>{String(flagged.length).padStart(2, "0")}</span>
      </h2>
      <ul className="rows">
        {flagged.map((u) => (
          <li key={u.userId} className="row admin-flag">
            <AlertTriangle size={18} className="admin-flag-icon" aria-hidden />
            <div className="admin-flag-main">
              <span className="row-title">{u.name ?? phone(u.phone)}</span>
              <span className="admin-flag-why">{u.flags.join(" · ")}</span>
            </div>
            <span className="row-when">{usd(u.costUsd)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// --- Per-user table -----------------------------------------------------------------------

type SortKey = "costUsd" | "inputTokens" | "outputTokens" | "calls" | "messages" | "avgInput" | "avgOutput" | "costPerMessage" | "audioTokens" | "audioCost";

const COLUMNS: Array<{ key: SortKey; label: string; title?: string; value: (u: UserUsage) => number; show: (u: UserUsage, usdBrl: number) => string }> = [
  { key: "messages", label: "Msgs", title: "Mensagens recebidas", value: (u) => u.messages, show: (u) => intFmt.format(u.messages) },
  { key: "calls", label: "Chamadas", title: "Chamadas à IA", value: (u) => u.calls, show: (u) => intFmt.format(u.calls) },
  { key: "inputTokens", label: "Input", value: (u) => u.inputTokens, show: (u) => tokens(u.inputTokens) },
  { key: "outputTokens", label: "Output", value: (u) => u.outputTokens, show: (u) => tokens(u.outputTokens) },
  { key: "avgInput", label: "Input/cham.", title: "Média de input por chamada", value: (u) => ratio(u.inputTokens, u.calls), show: (u) => tokens(ratio(u.inputTokens, u.calls)) },
  { key: "avgOutput", label: "Output/cham.", title: "Média de output por chamada", value: (u) => ratio(u.outputTokens, u.calls), show: (u) => tokens(ratio(u.outputTokens, u.calls)) },
  { key: "costPerMessage", label: "Custo/msg", value: (u) => ratio(u.costUsd, u.messages), show: (u) => usd(ratio(u.costUsd, u.messages)) },
  {
    key: "audioTokens",
    label: "Tokens áudio",
    title: "Tokens das mensagens por áudio (transcrição + interpretação), input / output",
    value: (u) => u.audio.inputTokens + u.audio.outputTokens,
    show: (u) => (u.audio.calls ? `${tokens(u.audio.inputTokens)} / ${tokens(u.audio.outputTokens)}` : "—")
  },
  {
    key: "audioCost",
    label: "Custo áudio",
    title: "Custo das mensagens por áudio (transcrição + interpretação)",
    value: (u) => u.audio.costUsd,
    show: (u) => (u.audio.calls ? usd(u.audio.costUsd) : "—")
  },
  { key: "costUsd", label: "Custo", value: (u) => u.costUsd, show: (u, r) => `${usd(u.costUsd)} · ${money(u.costUsd * r, "BRL")}` }
];

function UsersTable({ users, period, tz, usdBrl, limit }: { users: UserUsage[]; period: UsagePeriod; tz: string; usdBrl: number; limit: number }) {
  const [sort, setSort] = useState<SortKey>("costUsd");
  const [open, setOpen] = useState<string | null>(null);
  const col = COLUMNS.find((c) => c.key === sort)!;
  const rows = useMemo(() => [...users].sort((a, b) => col.value(b) - col.value(a)), [users, col]);

  return (
    <section className="admin-users" aria-label="Uso por usuário">
      <div className="col-head">
        <h2 className="col-title display">Por usuário</h2>
        <span className="row-when">{intFmt.format(users.length)} com atividade</span>
      </div>
      {!users.length ? (
        <p className="empty">Ninguém usou o assistente neste período.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col" className="admin-user-col">
                  Usuário
                </th>
                {COLUMNS.map((c) => (
                  <th key={c.key} scope="col" aria-sort={sort === c.key ? "descending" : undefined} title={c.title}>
                    <button className={`admin-sort${sort === c.key ? " on" : ""}`} onClick={() => setSort(c.key)}>
                      {c.label}
                      {sort === c.key && <ChevronDown size={12} aria-hidden />}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <Fragment key={u.userId}>
                  <tr className={open === u.userId ? "open" : undefined}>
                    <th scope="row" className="admin-user-col">
                      <button className="admin-user" onClick={() => setOpen(open === u.userId ? null : u.userId)} aria-expanded={open === u.userId}>
                        {open === u.userId ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
                        <span className="admin-user-text">
                          <span className="admin-user-name">
                            {u.name ?? "Sem nome"}
                            {u.flags.length > 0 && <AlertTriangle size={13} className="admin-flag-icon" aria-label="Uso fora do normal" />}
                            {u.blockedAt ? <span className="chip danger">bloqueado</span> : u.rateLimited && <span className="chip danger">pausado</span>}
                          </span>
                          <span className="admin-user-phone">{phone(u.phone)}</span>
                        </span>
                      </button>
                    </th>
                    {COLUMNS.map((c) => (
                      <td key={c.key} className={sort === c.key ? "on" : undefined}>
                        {c.show(u, usdBrl)}
                      </td>
                    ))}
                  </tr>
                  {open === u.userId && (
                    <tr className="admin-detail-row">
                      <td colSpan={COLUMNS.length + 1}>
                        <UserDetail user={u} period={period} tz={tz} limit={limit} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function UserDetail({ user, period, tz, limit }: { user: UserUsage; period: UsagePeriod; tz: string; limit: number }) {
  const { data } = useAdminUserUsage(user.userId, period);
  return (
    <div className="admin-detail">
      <dl className="admin-facts">
        <div>
          <dt>Cliente desde</dt>
          <dd>{dateTime(user.joinedAt, tz)}</dd>
        </div>
        <div>
          <dt>Última atividade</dt>
          <dd>{user.lastActivityAt ? dateTime(user.lastActivityAt, tz) : "—"}</dd>
        </div>
        <div>
          <dt>Pico por hora</dt>
          <dd>{intFmt.format(user.peakCallsPerHour)} chamadas</dd>
        </div>
        <div>
          <dt>Maior input</dt>
          <dd>{tokens(user.maxInputTokens)} tokens</dd>
        </div>
        <div>
          <dt>Input em cache</dt>
          <dd>{Math.round(ratio(user.cachedInputTokens, user.inputTokens) * 100)}%</dd>
        </div>
        <div>
          <dt>Sem resposta válida</dt>
          <dd>{intFmt.format(user.failedCalls)}</dd>
        </div>
        <div>
          <dt>Áudios recebidos</dt>
          <dd>
            {intFmt.format(user.audio.messages)}
            {user.audio.seconds > 0 && ` · ${duration(user.audio.seconds)}`}
          </dd>
        </div>
        <div>
          <dt>Tokens de áudio</dt>
          <dd>
            {tokens(user.audio.inputTokens)} in · {tokens(user.audio.outputTokens)} out
          </dd>
        </div>
        <div>
          <dt>Custo com áudio</dt>
          <dd>
            {usd(user.audio.costUsd)}
            {user.audio.costUsd > 0 && ` (${Math.round(ratio(user.audio.costUsd, user.costUsd) * 100)}% do usuário)`}
          </dd>
        </div>
      </dl>
      {user.flags.length > 0 && <p className="admin-flag-why">{user.flags.join(" · ")}</p>}
      <AccessControls user={user} limit={limit} tz={tz} />
      {!data ? (
        <div className="spinner" />
      ) : !data.calls.length ? (
        <p className="empty">Nenhuma chamada à IA neste período.</p>
      ) : (
        <>
          <h3 className="eyebrow admin-detail-title">Últimas chamadas{data.calls.length === 50 ? " (50 mais recentes)" : ""}</h3>
          <div className="admin-table-wrap">
            <table className="admin-table compact">
              <thead>
                <tr>
                  <th scope="col">Quando</th>
                  <th scope="col">Origem</th>
                  <th scope="col">Modelo</th>
                  <th scope="col">Input</th>
                  <th scope="col">Cache</th>
                  <th scope="col">Output</th>
                  <th scope="col">Reasoning</th>
                  <th scope="col">Custo</th>
                  <th scope="col">Tempo</th>
                </tr>
              </thead>
              <tbody>
                {data.calls.map((c, i) => (
                  <tr key={`${c.at}-${i}`} className={c.ok ? undefined : "failed"}>
                    <td>
                      {dateTime(c.at, tz)}
                      {!c.ok && <span className="chip danger">inválida</span>}
                    </td>
                    <td>{origin(c)}</td>
                    <td>{c.model}</td>
                    <td>{intFmt.format(c.inputTokens)}</td>
                    <td>{intFmt.format(c.cachedInputTokens)}</td>
                    <td>{intFmt.format(c.outputTokens)}</td>
                    <td>{intFmt.format(c.reasoningTokens)}</td>
                    <td>{usd(c.costUsd)}</td>
                    <td>{c.latencyMs === null ? "—" : `${(c.latencyMs / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} s`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
